/** Written by scripts/write-dev-stack-status.mjs during agent-driven registry restarts. */
export type DevStackProcessPhase = "restarting" | "ready";

export interface DevStackProcessStatus {
  phase: DevStackProcessPhase;
  sinceUtc: string;
}

export type DevStackStatusSnapshot = Partial<
  Record<"erp-api" | "erp-ui" | "erp-vendor-ui", DevStackProcessStatus>
>;

export const HUB_DEV_API_RESTARTING_MESSAGE =
  "Local API is restarting — new code from the agent will load in a moment.";

export const HUB_DEV_UI_RESTARTING_MESSAGE =
  "Local UI dev server is restarting — the page will reload when Vite is ready.";

export function parseDevStackStatusSnapshot(data: unknown): DevStackStatusSnapshot {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }
  const out: DevStackStatusSnapshot = {};
  for (const key of ["erp-api", "erp-ui", "erp-vendor-ui"] as const) {
    const row = (data as Record<string, unknown>)[key];
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      continue;
    }
    const phase = (row as { phase?: unknown }).phase;
    const sinceUtc = (row as { sinceUtc?: unknown }).sinceUtc;
    if (phase !== "restarting" && phase !== "ready") {
      continue;
    }
    if (typeof sinceUtc !== "string" || sinceUtc.trim() === "") {
      continue;
    }
    out[key] = { phase, sinceUtc: sinceUtc.trim() };
  }
  return out;
}

export function devStackRestartMessage(snapshot: DevStackStatusSnapshot): string | null {
  if (snapshot["erp-api"]?.phase === "restarting") {
    return HUB_DEV_API_RESTARTING_MESSAGE;
  }
  if (snapshot["erp-ui"]?.phase === "restarting") {
    return HUB_DEV_UI_RESTARTING_MESSAGE;
  }
  if (snapshot["erp-vendor-ui"]?.phase === "restarting") {
    return HUB_DEV_UI_RESTARTING_MESSAGE;
  }
  return null;
}

export function isDevStackRestarting(snapshot: DevStackStatusSnapshot): boolean {
  return devStackRestartMessage(snapshot) !== null;
}

export function devStackRestartMessageFromFlags(
  flags: Partial<Record<"erp-api" | "erp-ui" | "erp-vendor-ui", boolean>>,
): string | null {
  if (flags["erp-api"]) {
    return HUB_DEV_API_RESTARTING_MESSAGE;
  }
  if (flags["erp-ui"] || flags["erp-vendor-ui"]) {
    return HUB_DEV_UI_RESTARTING_MESSAGE;
  }
  return null;
}

export function isDevStackRestartingFromFlags(
  flags: Partial<Record<"erp-api" | "erp-ui" | "erp-vendor-ui", boolean>>,
): boolean {
  return devStackRestartMessageFromFlags(flags) !== null;
}
